/**
 * Typed facade over the six bridge endpoints (docs/PROTOCOL.md §3).
 *
 * `session.ts` calls these; nothing else in the app builds request bodies,
 * so a protocol change is a one-file edit plus this file's tests.
 */
import { bridgeCall } from "./client.ts";
import type {
  Conn,
  FlagSet,
  FolderInfo,
  MessagesPage,
  RawMessage,
  SearchCriteria,
  SearchHits,
  SendReceipt,
  SendRequest,
} from "./types.ts";

/**
 * Longest page the SPA ever asks for; matches the server ceiling.
 */
export const PAGE_LIMIT = 50;

/**
 * Largest number of explicit UIDs sent in one summarise call. Search is
 * capped to this many hits client-side so a `text` match across a huge
 * mailbox cannot turn into a thousand-UID literal.
 */
export const SEARCH_SUMMARY_CAP = 200;

/**
 * Body of `POST /api/imap/messages`: either a paging window or an explicit
 * UID set — never both.
 */
type MessagesBody = {
  readonly conn: Conn;
  readonly mailbox: string;
} & (
  | { readonly page: { readonly limit: number; readonly uidFrom?: number } }
  | { readonly uids: readonly number[] }
);

/**
 * Lists the account's mailboxes with their counters.
 *
 * @param conn - One-time credentials.
 * @returns Folders, `INBOX` first.
 */
export async function fetchFolders(conn: Conn): Promise<readonly FolderInfo[]> {
  const data = await bridgeCall<{ folders: readonly FolderInfo[] }>(
    "/api/imap/folders",
    { conn },
  );
  return data.folders;
}

/**
 * Fetches one page of summaries, newest first (`limit` UIDs below the top).
 *
 * @param conn - One-time credentials.
 * @param mailbox - Server mailbox name.
 * @param uidFrom - Exclusive lower bound; omit for the newest page.
 * @returns The page with the mailbox's `uidValidity` and `highestUid`.
 */
export async function fetchMessagePage(
  conn: Conn,
  mailbox: string,
  uidFrom?: number,
): Promise<MessagesPage> {
  const page: { limit: number; uidFrom?: number } = { limit: PAGE_LIMIT };
  if (uidFrom !== undefined) {
    page.uidFrom = uidFrom;
  }
  const body: MessagesBody = { conn, mailbox, page };
  return bridgeCall<MessagesPage>("/api/imap/messages", body);
}

/**
 * Fetches summaries for explicit UIDs (search results).
 *
 * @param conn - One-time credentials.
 * @param mailbox - Server mailbox name.
 * @param uids - UIDs to summarise, ascending, capped by the caller.
 * @returns The page-style payload for exactly those UIDs.
 */
export async function fetchMessagesByUids(
  conn: Conn,
  mailbox: string,
  uids: readonly number[],
): Promise<MessagesPage> {
  const body: MessagesBody = { conn, mailbox, uids: [...uids] };
  return bridgeCall<MessagesPage>("/api/imap/messages", body);
}

/**
 * Fetches one full raw message for local parsing.
 *
 * @param conn - One-time credentials.
 * @param mailbox - Server mailbox name.
 * @param uid - Message UID.
 * @returns The raw RFC 5322 bytes.
 */
export async function fetchRawMessage(
  conn: Conn,
  mailbox: string,
  uid: number,
): Promise<ArrayBuffer> {
  const data = await bridgeCall<RawMessage>("/api/imap/message", {
    conn,
    mailbox,
    uid,
  });
  return base64ToBuffer(data.rawBytes);
}

/**
 * Adds and removes flags on one message.
 *
 * @param conn - One-time credentials.
 * @param mailbox - Server mailbox name.
 * @param uid - Message UID.
 * @param add - Flags to add (`\Seen`, `\Flagged`).
 * @param remove - Flags to clear.
 * @returns The flag set after the store.
 */
export async function updateFlags(
  conn: Conn,
  mailbox: string,
  uid: number,
  add: readonly string[],
  remove: readonly string[] = [],
): Promise<readonly string[]> {
  const data = await bridgeCall<FlagSet>("/api/imap/flags", {
    conn,
    mailbox,
    uid,
    add: [...add],
    remove: [...remove],
  });
  return data.flags;
}

/**
 * Runs a structured search.
 *
 * @param conn - One-time credentials.
 * @param mailbox - Server mailbox name.
 * @param criteria - The closed-union criteria.
 * @returns Matching UIDs, ascending.
 */
export async function searchMailbox(
  conn: Conn,
  mailbox: string,
  criteria: SearchCriteria,
): Promise<readonly number[]> {
  const data = await bridgeCall<SearchHits>("/api/imap/search", {
    conn,
    mailbox,
    criteria,
  });
  return data.uids;
}

/**
 * Sends a message over SMTP.
 *
 * Never retried automatically: the caller decides what a failure means
 * (queue, show, discard) and a replayed send could duplicate a message that
 * actually went out.
 *
 * @param conn - One-time credentials (SMTP side).
 * @param request - The outgoing message.
 * @returns The server receipt.
 */
export function sendMessage(
  conn: Conn,
  request: SendRequest,
): Promise<SendReceipt> {
  return bridgeCall<SendReceipt>(
    "/api/smtp/send",
    { conn, ...request },
    { idempotent: false },
  );
}

/**
 * Decodes standard base64 into a byte buffer.
 *
 * @param text - Base64 text as the bridge sends it.
 * @returns The decoded buffer.
 */
export function base64ToBuffer(text: string): ArrayBuffer {
  const binary = atob(text);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < binary.length; index += 1) {
    // eslint-disable-next-line unicorn/prefer-code-point -- byte-level decode of atob output
    bytes[index] = binary.charCodeAt(index);
  }
  return buffer;
}
